if (!window.__BuyingJourneyController) {
  // Map question metaobject handles -> short cart-attribute keys.
  // When a new question metaobject is added, add it here too — otherwise the
  // raw handle becomes the cart key, which is ugly and may exceed Shopify's
  // 255-char attribute key limit for long handles.

  const QUESTION_KEY_MAP = {
    "how-many-days-a-week-do-you-want-your-meal-plan": "days",
    "how-many-meals-per-day": "meals",
    "breakfast-boost": "breakfast",
    "snacks": "snacks",
    "any-dietary-preferences": "preferences",
    "select-dietary-preferences": "preferences",
    "the-no-go-list": "allergens",
    "select-ingredients-to-avoid": "avoid",
  };

  // The cart-attribute keys (unprefixed) that hold a plan's configuration, mirrored from
  // CONFIG_RULES in section-plan-builder-deep-link.js. Selecting a new plan must clear
  // these — see the comment on clearedConfigAttributes() below.
  const CONFIG_ATTR_KEYS = ["days", "meals", "breakfast", "snacks","preferences", "allergens", "avoid" ];

  // A plan card can carry a preset configuration in data-plan-preset: the same query
  // string as its builder deep link ("days=5&meals=1&breakfast=1&snacks=1"), whose keys
  // are cart-attribute keys — the values of QUESTION_KEY_MAP. The Workday plan uses it
  // to fix the week while still asking preferences and allergens.
  //
  // Normalisation and labels mirror section-plan-builder-deep-link.js, so a plan
  // configured through the quiz reads the same on the order as one opened from an
  // email deep link. Kept in step by hand: that script only loads on the builder page.
  const PRESET_BOOL_KEYS = ["breakfast", "snacks", "allergens"];
  const PRESET_TRUTHY = ["1", "true", "yes"];

  const parsePreset = (raw) => {
    const preset = {};
    if (!raw) return preset;
    new URLSearchParams(raw).forEach((value, key) => {
      if (!CONFIG_ATTR_KEYS.includes(key)) return;
      const trimmed = value.trim();
      if (!trimmed) return;
      preset[key] = PRESET_BOOL_KEYS.includes(key)
        ? PRESET_TRUTHY.includes(trimmed.toLowerCase()) ? "1" : "0"
        : trimmed;
    });
    // An ingredient list means restrictions, whatever `allergens` claimed.
    if (preset.avoid) preset.allergens = "1";
    return preset;
  };

  const presetLabel = (key, value) => {
    if (key === "days") return `${value} Days`;
    if (key === "meals") return `${value} Meal${value === "1" ? "" : "s"}`;
    if (PRESET_BOOL_KEYS.includes(key)) return value === "1" ? "Yes" : "No";
    return value;
  };

  const rootUrl = () => (window.Shopify?.routes?.root) || "/";

  const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)");

  // Beat between a choice registering and the wizard moving on. Without it the
  // step swaps on mousedown-release and the user never sees what they picked.
  // Kept just under the confirm animation so the exit overlaps its tail.
  const CONFIRM_MS = 260;
  // Length of the bj-confirm keyframes (--duration-slow), used to clear the class.
  const CONFIRM_ANIM_MS = 320;
  // Mirrors --duration-fast in section-buying-journey.css.
  const EXIT_MS = 180;
  // Longest entrance chain: 70ms delay + 7 staggered cards * 45ms + 320ms.
  const ENTER_MS = 750;

  class BuyingJourneyController {
    constructor(root) {
      this.root = root;
      this.attrPrefix = root.dataset.cartAttrPrefix ?? "";
      this.steps = Array.from(root.querySelectorAll("[data-step]"));
      this.stepsEl = root.querySelector("[data-steps]");
      this.stepperWrapper = root.querySelector(".buying-journey__stepper-wrapper");
      this.stepperItems = Array.from(root.querySelectorAll("[data-stepper] [data-step-handles]"));
      this.planJourneys = this.parsePlanJourneys();

      this.selectedPlan = null;
      this.sequence = ["plan"];
      this.currentIndex = 0;
      this.subStack = [];

      // The deepest step reached, which is what decides whether a stepper chip can be
      // clicked. Deliberately NOT the same as the is-complete class: that is positional
      // and drops off every chip ahead of you the moment you step back, so gating on it
      // would make a step you already answered unreachable until you clicked Next
      // through it again.
      this.furthestIndex = 0;

      // True from the moment a choice is confirmed until the next step has
      // landed, so a double-click can't start a second transition mid-flight.
      this.isTransitioning = false;
      this._timers = new Set();

      // Debounced multi-select cart writes, keyed by question handle — one
      // timer per question, never one for the whole controller. See the
      // comment on queueSelectionWrite().
      this._writeTimers = new Map();
      this._pendingWrites = new Map();

      this._handleClick = this._handleClick.bind(this);
      this.root.addEventListener("click", this._handleClick);

      
      this.restoreFromCart();
      this.openRequestedStep();
    }

    destroy() {
      this.root.removeEventListener("click", this._handleClick);
      this._timers.forEach(clearTimeout);
      this._timers.clear();
      this._writeTimers.forEach(clearTimeout);
      this._writeTimers.clear();
    }

    reducedMotion() {
      return REDUCED_MOTION.matches;
    }

    // setTimeout that cancels itself if the section is torn down mid-transition
    // (theme editor section reload).
    after(ms, fn) {
      const id = setTimeout(() => {
        this._timers.delete(id);
        fn();
      }, ms);
      this._timers.add(id);
      return id;
    }

    parsePlanJourneys() {
      const script = this.root.querySelector("[data-plan-journeys]");
      if (!script) return {};
      try {
        return JSON.parse(script.textContent.trim()) || {};
      } catch (e) {
        console.error("[buying-journey] failed to parse plan journeys", e);
        return {};
      }
    }

    parseCartAttributes() {
      const script = this.root.querySelector("[data-bj-cart-attributes]");
      if (!script) return {};
      try {
        return JSON.parse(script.textContent.trim()) || {};
      } catch (e) {
        console.error("[buying-journey] failed to parse cart attributes", e);
        return {};
      }
    }

    
    onRestoreMismatch(reason, detail) {
      console.warn(`[buying-journey] cannot restore ${reason} from cart`, detail);
      return false;
    }

    restoreFromCart() {
      const attrs = this.parseCartAttributes();
      const planHandle = attrs[`${this.attrPrefix}plan`];
      if (!planHandle) return;

      const card = this.root.querySelector(
        `[data-plan-handle="${CSS.escape(planHandle)}"]`
      );
      if (!card) {
        this.onRestoreMismatch("plan", { handle: planHandle });
        return;
      }

      this.selectedPlan = planHandle;
      this.selectedProductHandle = card.dataset.planProductHandle;
     
      card.classList.add("is-selected");
      const preset = parsePreset(card.dataset.planPreset);
      this.sequence = this.sequenceFor(planHandle, preset);

      // Blocks on a step the preset dropped from the sequence are not restored: the
      // step never shows, and a preset value can be one the quiz does not offer
      // (Workday's meals=1), which would only log a mismatch for an answer nobody can
      // reach. Derived from the sequence rather than recomputed from the groups: two
      // groups can resolve to the same rendered step, and if one is preset while the
      // other is still asked, the step is shown and must restore like any other.
      const skipped = this.stepHandlesFor(this.planJourneys[planHandle] || []).filter(
        (handle) => !this.sequence.includes(handle)
      );
      const blocks = Array.from(this.root.querySelectorAll("[data-question-block]")).filter(
        (b) => !skipped.includes(b.closest("[data-step]")?.dataset.step)
      );
    
      blocks
        .filter((b) => !b.hasAttribute("data-question-sub"))
        .forEach((b) => this.restoreBlock(b, attrs));
      blocks
        .filter((b) => b.hasAttribute("data-question-sub"))
        .forEach((b) => this.restoreBlock(b, attrs));

      // How far they actually got, measured in restored answers rather than assumed from
      // the sequence length: a cart holding a plan the customer picked and then abandoned
      // must not light up every chip as somewhere they have been. Returning via ?step=last
      // raises this to the end anyway, through goToIndex.
      this.furthestIndex = this.sequence.reduce((furthest, handle, idx) => {
        const stepEl = this.steps.find((s) => s.dataset.step === handle);
        return stepEl?.querySelector("[data-answer-select].is-selected") ? idx : furthest;
      }, 0);

      // goToIndex is what normally paints the stepper, and a plain visit never calls it —
      // the customer stays on the plan picker. Paint it here so the chips are live on
      // arrival instead of only after the first move.
      this.updateStepper(this.sequence[this.currentIndex]);
    }

    
    restoreBlock(block, attrs) {
     
      const stepEl = block.closest("[data-step]");
      if (
        block.hasAttribute("data-question-disabled") &&
        stepEl &&
        this.isGroupStep(stepEl)
      ) {
        return;
      }

      const handle = block.dataset.questionHandle;
      const stored = attrs[this.cartKeyForStep(handle)];
      if (!stored) return;

      const buttons = Array.from(block.querySelectorAll("[data-answer-select]"));

      if (block.dataset.questionType === "multi-select") {
        const labels = new Set(
          stored.split(",").map((s) => s.trim()).filter(Boolean)
        );
        const matched = buttons.filter((b) =>
          labels.has((b.dataset.answerLabel || "").trim())
        );
        if (matched.length !== labels.size) {
          if (this.onRestoreMismatch("answer", { handle, stored })) return;
        }
        matched.forEach((b) => {
          b.classList.add("is-selected");
          b.setAttribute("aria-checked", "true");
        });
        return;
      }

      const btn = buttons.find((b) => b.dataset.answerValue === stored);
      if (!btn) {
        this.onRestoreMismatch("answer", { handle, stored });
        return;
      }
      btn.classList.add("is-selected");
      if (btn.getAttribute("aria-checked")) btn.setAttribute("aria-checked", "true");
      // An answer that owns a sub-question has to un-gate it, or the sub's own restored
      // selections sit in a block the CSS hides and requiredQuestionsAnswered() exempts.
      if (btn.dataset.answerSub) this.revealSub(block, btn.dataset.answerSub);
    }

   
    openRequestedStep() {
      const params = new URLSearchParams(window.location.search);
      if (params.get("step") !== "last") return;
    
      if (this.sequence.length < 2) {
        this.root.classList.remove("is-jumping");
        return;
      }

     
      const rendered = this.steps.find((s) => s.classList.contains("is-active"));
      if (rendered) {
        rendered.classList.remove("is-active");
        rendered.setAttribute("hidden", "");
      }

      this.goToIndex(this.sequence.length - 1, "back");

      
      let active = this.steps.find((s) => s.classList.contains("is-active"));
      if (!active && rendered) {
        rendered.removeAttribute("hidden");
        rendered.classList.add("is-active");
        active = rendered;
      }

      
      if (active) this.updateNextState(active);

      this.root.classList.remove("is-jumping");
    }

    _handleClick(event) {
      // Guard a QUIZ plan CTA before selectPlan() writes `plan` to the cart -- a dialog
      // any later is too late, the previous journey is already gone.
      //
      // Pure deep-link CTAs (a plain <a> with no `data-plan-select`) are deliberately NOT
      // guarded here. They only navigate -- nothing is written to the cart on this page
      // -- and the plan builder they land on runs the same guard in its deep-link
      // reconciler before it patches the cart. Guarding both showed the dialog twice.
      // A preset plan that still asks questions DOES carry data-plan-select: its click
      // writes the preset to the cart right here, so it is guarded like any quiz plan.
      const planCta = event.target.closest("[data-plan-cta][data-plan-select]");
      // Re-picking the plan already in progress destroys nothing, so it is not a restart
      // and must not be dressed as one. Without this, stepping back to the plan picker
      // and clicking the same card offered to resume a journey the customer is standing
      // in — and put "Start a new plan", which now clears their cart, one click away.
      const ctaPlan = planCta?.closest("[data-plan-handle]")?.dataset.planHandle;
      if (
        planCta &&
        ctaPlan !== this.selectedPlan &&
        !this._resumeAsked &&
        window.JourneyResume?.hasJourney()
      ) {
        event.preventDefault();
        this._resumeAsked = true;
        // Resolves only if they choose to start over; the resume path navigates away.
        window.JourneyResume.ask().then(() => {
          // They have just accepted losing the previous journey. Clearing the answers on
          // screen keeps selectPlan()'s own guard from asking the same question twice.
          this.resetAnswerSelections();
          planCta.click();
        });
        return;
      }

      const planBtn = event.target.closest("[data-plan-select]");
      const ansBtn = event.target.closest("[data-answer-select]");
      const navBtn = event.target.closest("[data-step-next], [data-step-prev]");
      const stepperBtn = event.target.closest("[data-stepper-nav]");
      if (!planBtn && !ansBtn && !navBtn && !stepperBtn) return;

      event.preventDefault();
      // Swallow anything that lands while a step is on the move.
      if (this.isTransitioning) return;

      if (stepperBtn) {
        this.goToStepperItem(stepperBtn.closest("[data-step-handles]"));
        return;
      }
      if (planBtn) {
        this.selectPlan(planBtn.closest("[data-plan-handle]"));
        return;
      }
      if (ansBtn) {
        this.selectAnswer(ansBtn);
        return;
      }
      if (navBtn.hasAttribute("data-step-next")) {
        this.advance();
        return;
      }
      this.goPrev();
    }

    cartKeyForStep(stepHandle) {
      if (stepHandle === "plan") return `${this.attrPrefix}plan`;
      const mapped = QUESTION_KEY_MAP[stepHandle];
      if (!mapped) {
      
        console.error(
          `[buying-journey] no QUESTION_KEY_MAP entry for "${stepHandle}" — the answer will be written to cart attribute "${stepHandle}", which no template reads. Add it to QUESTION_KEY_MAP in assets/section-buying-journey.js.`
        );
      }
      return `${this.attrPrefix}${mapped || stepHandle}`;
    }

    // A plan's journey names QUESTION handles; the DOM has STEP handles. Those are
    // usually the same string, but not always, and the difference is not cosmetic:
    //
    // All plans share ONE set of rendered steps, built from the canonical (is_popular)
    // plan — see the comment on primary_plan in sections/buying-journey.liquid. A step
    // is keyed by the FIRST question of its group *in that plan's order*. Any other
    // plan that lists the same group's questions in a different order names the group
    // by a different question, and an exact `data-step` match finds nothing.
    //
    // Concretely: the steps are keyed "how-many-days-a-week..." (Ultimate Performance's
    // order), but Balanced Plan leads with "how-many-meals-per-day". That resolved to no
    // step at all, so the wizard sat on the plan picker while the stepper advanced.
    //
    // So resolve by CONTAINMENT — the step that renders this question — and fall back
    // to the group's remaining questions. The invariant the Liquid documents (equal
    // step_labels, adjacent questions, every plan) is enforced in the admin by nothing,
    // so the JS must not assume it holds.
    stepHandleFor(group) {
      const candidates = [group.handle, ...(group.questions || [])].filter(Boolean);
      for (const handle of candidates) {
        if (this.steps.some((s) => s.dataset.step === handle)) return handle;
        const owner = this.steps.find((s) =>
          s.querySelector(`[data-question-block][data-question-handle="${CSS.escape(handle)}"]`)
        );
        if (owner) return owner.dataset.step;
      }
      return null;
    }

    // Several of a plan's groups can resolve onto the same rendered step (the display
    // order inside a group is re-sorted by the question_order_first setting, independently
    // of journey order). Collapse those: `sequence` must hold each step exactly once, or
    // advance() walks to an index that shows the screen the user is already on.
    stepHandlesFor(journey) {
      const seen = new Set();
      const handles = [];
      journey.forEach((group) => {
        const stepHandle = this.stepHandleFor(group);
        if (!stepHandle) {
          console.warn(
            `[buying-journey] journey group "${group.handle}" matches no rendered step — skipping.`,
            "Its questions are not drawn by the canonical plan; check journey_questions/step_label in the admin."
          );
          return;
        }
        if (seen.has(stepHandle)) return;
        seen.add(stepHandle);
        handles.push(stepHandle);
      });
      return handles;
    }

    // The steps a plan walks, minus any its preset answers in full. Decided per group:
    // a step is skipped only when EVERY question it renders has a preset value. A
    // preset covering days and meals but not snacks still shows the Your Week step and
    // asks all of it; nothing on a shown step is pre-lit from the preset.
    // With Workday's preset this yields ["plan", <preferences step>]: the customer lands
    // on Preferences, Previous returns to the plan picker, and the Your Week chip stays
    // locked because updateStepperNav() disables any chip outside the sequence.
    sequenceFor(planHandle, preset = {}) {
      const journey = this.planJourneys[planHandle] || [];
      const asked = journey.filter((group) => {
        const questions = group.questions || [];
        if (!questions.length) return true;
        return !questions.every((q) => preset[QUESTION_KEY_MAP[q]] !== undefined);
      });
      return ["plan", ...this.stepHandlesFor(asked)];
    }

    // Cart attributes for a preset, value and merchant-facing label per key, in the
    // same shape selectAnswer() and the deep-link reconciler write.
    presetAttributes(preset) {
      const attrs = {};
      Object.entries(preset).forEach(([key, value]) => {
        const prefixed = `${this.attrPrefix}${key}`;
        attrs[prefixed] = value;
        attrs[`${prefixed}_label`] = presetLabel(key, value);
      });
      return attrs;
    }

    // Resolves true if the customer accepts losing their answers, false to keep them.
    //
    // Unlike journey-resume this dialog IS dismissible: keeping the answers is a real and
    // safe choice, so Escape and the backdrop both mean "keep". Focus goes to the keeping
    // option and returns to whatever opened the dialog on close.
    confirmPlanChange() {
      const modal = this.root.querySelector("[data-plan-change]");
      // A missing dialog is a theme error, not a customer decision. Fail open rather than
      // stranding them on step 1 unable to choose a plan at all — the same call
      // journey-resume's ask() makes about its own element.
      if (!modal) return Promise.resolve(true);

      return new Promise((resolve) => {
        const opener = document.activeElement;
        modal.removeAttribute("hidden");
        const release = window.FocusTrap ? window.FocusTrap.trap(modal) : () => {};
        modal.querySelector("[data-pc-cancel]")?.focus();

        const close = (accepted) => {
          release();
          document.removeEventListener("keydown", onKeydown);
          modal.removeEventListener("click", onClick);
          modal.setAttribute("hidden", "");
          if (opener instanceof HTMLElement) opener.focus();
          resolve(accepted);
        };

        const onKeydown = (event) => {
          if (event.key === "Escape") close(false);
        };
        const onClick = (event) => {
          if (event.target.closest("[data-pc-confirm]")) return close(true);
          if (event.target.closest("[data-pc-cancel]")) return close(false);
          // Anything inside the panel that is not a button is not an answer.
          if (event.target.closest(".journey-resume__panel")) return;
          close(false);
        };

        document.addEventListener("keydown", onKeydown);
        modal.addEventListener("click", onClick);
      });
    }

    // Any answer showing on screen, whether the customer clicked it this page load or
    // restoreFromCart() painted it. This — not journey-resume's server-rendered
    // has_journey — is what says whether picking a plan would destroy something. Liquid
    // computes has_journey once, while the page renders, so it cannot see a journey the
    // customer builds after that: on a fresh visit it is false for the whole session.
    hasAnyAnswer() {
      return Boolean(this.root.querySelector("[data-answer-select].is-selected"));
    }

    selectPlan(card) {
      if (!card) return;
      const handle = card.dataset.planHandle;
      const productId = card.dataset.planProductId;
      const productHandle = card.dataset.planProductHandle;
      if (!handle) return;
      const preset = parsePreset(card.dataset.planPreset);

      // Re-picking the plan they are already on. Nothing is changing, so nothing should
      // be destroyed: carry the answers forward and just move to the first question.
      // Clearing here is what wiped a customer's quiz for stepping back to look at the
      // plans — selectPlan() used to clear unconditionally, which was only safe while
      // step 1 could not be revisited with answers already given.
      if (handle === this.selectedPlan && this.hasAnyAnswer()) {
        this.selectedProductHandle = productHandle;
        this.sequence = this.sequenceFor(handle, preset);
        this.subStack = [];
        this.dispatchSelected("plan", handle, { productId, productHandle });
        this.confirm(card, () => this.goToIndex(1));
        // No cart write: the plan attribute already holds this handle, and the answers
        // must survive untouched.
        return;
      }

      // A genuine switch that would throw answers away. journey-resume's guard in
      // _handleClick only fires for a journey that existed at render time, so this is
      // the only thing standing between an in-page journey and a silent wipe.
      if (handle !== this.selectedPlan && this.hasAnyAnswer()) {
        this.confirmPlanChange().then((ok) => {
          if (!ok) return;
          this.resetAnswerSelections();
          this.selectPlan(card);
        });
        return;
      }

      this.selectedPlan = handle;
      this.selectedProductHandle = productHandle;
      this.sequence = this.sequenceFor(handle, preset);
      this.subStack = [];
      // A new plan is a new journey. Without this the chips a RESTORED journey unlocked
      // would stay clickable over a configuration that no longer has any answers behind
      // them — goToIndex only ever raises this value.
      this.furthestIndex = 0;
      this.resetAnswerSelections();
      this.dispatchSelected("plan", handle, { productId, productHandle });
      this.confirm(card, () => this.goToIndex(1));

      // The preset lands in the same write, after the clear so it wins. Registered in
      // _pendingWrites: a plan whose journey the preset answers in full has no step to
      // show, so goToIndex(1) above goes straight to completeJourney(), which must not
      // navigate to the builder before this write has reached the cart.
      const write = this.updateCartAttributes({
        [`${this.attrPrefix}plan`]: handle,
        ...this.clearedConfigAttributes(),
        ...this.presetAttributes(preset),
      }).catch((err) => console.error("[buying-journey] failed to save plan", err));
      this._pendingWrites.set("plan", write);
    }

    
    resetAnswerSelections() {
      this.root.querySelectorAll("[data-question-block]").forEach((block) => {
        block.querySelectorAll("[data-answer-select].is-selected").forEach((btn) => {
          btn.classList.remove("is-selected");
          if (btn.getAttribute("aria-checked")) btn.setAttribute("aria-checked", "false");
        });
        // Re-gate the sub-questions: the parent answer that opened them is gone.
        if (block.hasAttribute("data-question-sub")) {
          block.setAttribute("data-question-disabled", "");
          block.setAttribute("aria-disabled", "true");
        }
      });
    }

    // Choosing a plan means starting a fresh configuration. Without this, the cart keeps
    // whatever days/meals/breakfast/snacks/allergens/avoid/selections a PREVIOUS plan left
    // behind, producing a hybrid the customer never chose: journey-resume.liquid reads
    // has_config/has_selections off these same keys and offers a "continue where I left
    // off" that section-plan-builder.js's restoreSelections() silently refuses (its saved
    // payload's `plan` no longer matches), landing the customer on an empty grid.
    //
    // This page (sections/buying-journey.liquid) does not expose cart.attributes to JS —
    // unlike the plan builder page, which reads them via [data-pb-cart-attributes] — so
    // there's no cheap way to compare against the cart's current plan before clearing.
    // Clearing unconditionally is safe: a fresh visitor has nothing to clear, and the
    // quiz overwrites these same keys immediately after the customer answers anyway.
    clearedConfigAttributes() {
      const cleared = {};
      CONFIG_ATTR_KEYS.forEach((key) => {
        const prefixed = `${this.attrPrefix}${key}`;
        cleared[prefixed] = "";
        cleared[`${prefixed}_label`] = "";
      });
      // Written unprefixed by section-plan-builder.js regardless of attrPrefix.
      cleared.selections = "";
      return cleared;
    }

    // Plays the selection beat on the chosen card, then hands off. The cart
    // write above runs in parallel — the animation is never waiting on network.
    //
    // Cleanup is on a timer, not `animationend`: the step is hidden shortly
    // after, and a display:none subtree stops animating without ever firing the
    // event, which would strand `.is-confirming` on the card and replay a stray
    // pulse if the user navigated back to it. Timers also survive a hidden tab.
    confirm(el, next) {
      if (this.reducedMotion()) {
        next();
        return;
      }
      this.isTransitioning = true;
      el.classList.add("is-confirming");
      this.after(CONFIRM_ANIM_MS, () => el.classList.remove("is-confirming"));
      this.after(CONFIRM_MS, next);
    }

    // The selection beat WITHOUT confirm()'s transition lock. confirm() sets
    // isTransitioning and relies on the step swap to clear it; on a grouped step
    // there is no swap, so the flag would stay set and _handleClick would swallow
    // every later click — the answer could never be changed.
    pulse(el) {
      if (this.reducedMotion()) return;
      el.classList.add("is-confirming");
      this.after(CONFIRM_ANIM_MS, () => el.classList.remove("is-confirming"));
    }

    blockFor(el) {
      return el.closest("[data-question-block]");
    }

    // A step can carry several questions when they share a step_label, so the
    // question a click belongs to is the enclosing block, not the step.
    isGroupStep(stepEl) {
      return parseInt(stepEl.dataset.groupSize || "1", 10) > 1;
    }

    // Next unlocks once every REQUIRED question on the screen is answered.
    //
    // A LOCKED sub-question never counts. data-question-disabled drops it from
    // the filter entirely, so a screen whose sub-question is closed by design
    // can still reach Next.
    //
    // Among what is left, the question TYPE is the switch — single-select is
    // required, multi-select is not — with one exception, below. Nothing is
    // hardcoded per handle.
    //
    // A TOP-LEVEL multi-select stays optional: picking nothing is a real answer
    // meaning "none", and no multi-select in the journey ships an explicit
    // opt-out card, so requiring a pick would strand anyone with no dietary
    // preferences and no allergens.
    //
    // An UNLOCKED SUB multi-select IS required, and that is the exception. It
    // is only on screen because the customer answered "Yes" to its parent, so
    // "yes, I have foods to avoid" with nothing ticked is a contradiction, not
    // an answer — and it would send an empty attribute to the cart. The opt-out
    // here is the parent's "No" card, which re-locks and clears the block
    // (revealSub), which is also what re-enables Next.
    requiredQuestionsAnswered(stepEl) {
      return Array.from(stepEl.querySelectorAll("[data-question-block]"))
        .filter((b) => !b.hidden && !b.hasAttribute("data-question-disabled"))
        .filter(
          (b) =>
            b.dataset.questionType !== "multi-select" ||
            b.hasAttribute("data-question-sub")
        )
        .every((b) => b.querySelector("[data-answer-select].is-selected"));
    }

    updateNextState(stepEl) {
      const btn = stepEl.querySelector("[data-step-next]");
      if (!btn) return;
      const hasMulti = stepEl.querySelector(
        '[data-question-block][data-question-type="multi-select"]'
      );
      // Next belongs on any grouped step, and on a lone multi-select the way it
      // always has. A lone single-select still auto-advances instead.
      const needsNext = this.isGroupStep(stepEl) || Boolean(hasMulti);
      btn.hidden = !needsNext;
      if (!needsNext) return;
      const ready = this.requiredQuestionsAnswered(stepEl);
      btn.disabled = !ready;
      btn.classList.toggle("is-disabled", !ready);
      // The last step turns Next into the journey's closing CTA: it takes the
      // "See My Plan" label and keeps Next's position (Previous left, CTA right).
      // The sequence is built from the answers given, so only the controller
      // knows which step is last — Liquid's forloop.last would be wrong here.
      const isFinal = this.currentIndex === this.sequence.length - 1;
      btn.classList.toggle("buying-journey__next--final", isFinal);
      const label = btn.querySelector(".buying-journey__next-label");
      if (label) {
        label.textContent = isFinal ? "Select Meals" : "Next";
      }
    }

    // Inside a group the sub-question is a sibling block, not a step. It starts
    // HIDDEN and is revealed by the answer that owns it, so the step only ever
    // shows cards the customer can actually pick. data-question-disabled is the
    // single switch: section-buying-journey.css hides the block on it, and
    // requiredQuestionsAnswered() exempts it from gating Next while it is set.
    //
    // The buttons are disabled here too, not left to CSS alone: the attribute is
    // also what keeps a stale selection out of the cart if the styles ever fail
    // to load, and switching back to "No" must clear what "Yes" collected.
    // Only the sub-questions THIS question owns are touched. A group can gate
    // more than one (step 3 gates dietary preferences and the no-go list), and
    // sweeping every [data-question-sub] on the step would close — and clear —
    // the other question's sub the moment this one is answered. Ownership is
    // read off the answers' data-answer-sub, the same source
    // clearedSubAttributes() uses for the cart side of the reset.
    revealSub(block, subHandle) {
      const owned = new Set(
        Array.from(block.querySelectorAll("[data-answer-select][data-answer-sub]"))
          .map((b) => b.dataset.answerSub)
          .filter(Boolean)
      );
      if (!owned.size) return;
      const stepEl = block.closest("[data-step]");
      if (!stepEl) return;

      stepEl.querySelectorAll("[data-question-sub]").forEach((sub) => {
        if (!owned.has(sub.dataset.questionHandle)) return;
        const match = sub.dataset.questionHandle === subHandle;
        sub.toggleAttribute("data-question-disabled", !match);
        sub.setAttribute("aria-disabled", match ? "false" : "true");
        sub.querySelectorAll("[data-answer-select]").forEach((b) => {
          b.disabled = !match;
        });
        if (match) return;
        sub.querySelectorAll("[data-answer-select].is-selected").forEach((b) => {
          b.classList.remove("is-selected");
          if (b.getAttribute("aria-checked")) b.setAttribute("aria-checked", "false");
        });
      });
    }

    selectAnswer(btn) {
      const stepEl = btn.closest("[data-step]");
      const block = this.blockFor(btn);
      if (!stepEl || !block) return;
      const qHandle = block.dataset.questionHandle;
      const key = this.cartKeyForStep(qHandle);

      if (block.dataset.questionType === "multi-select") {
        const isSelected = btn.classList.toggle("is-selected");
        btn.setAttribute("aria-checked", isSelected ? "true" : "false");

        const labels = this.selectedLabels(block);
        this.queueSelectionWrite(block);
        this.dispatchSelected(qHandle, labels.join(","), { labels });
        this.updateNextState(stepEl);
        return;
      }

      const value = btn.dataset.answerValue;
      const label = btn.dataset.answerLabel || "";
      const subHandle = btn.dataset.answerSub;

      block.querySelectorAll("[data-answer-select]").forEach((b) => {
        b.classList.remove("is-selected", "is-confirming");
      });
      btn.classList.add("is-selected");

      const attrs = { [key]: value };
      if (label) attrs[`${key}_label`] = label;
      // A sibling answer may open a sub-question (no-go list "Yes" -> ingredients).
      // Picking a different answer must wipe whatever that sub-question stored.
      Object.assign(attrs, this.clearedSubAttributes(block, subHandle));

      this.updateCartAttributes(attrs).catch((err) =>
        console.error("[buying-journey] failed to save answer", err)
      );
      this.dispatchSelected(qHandle, value, { label, subHandle });

      // Inside a group the sub-question is a sibling block on this same screen,
      // and advancing on click would skip the questions below it. Reveal and stay.
      if (this.isGroupStep(stepEl)) {
        this.revealSub(block, subHandle);
        this.updateNextState(stepEl);
        this.pulse(btn);
        return;
      }

      this.confirm(btn, () => {
        if (subHandle) {
          const subIndex = this.steps.findIndex((s) => s.dataset.step === subHandle);
          if (subIndex >= 0) {
            this.subStack.push(this.currentIndex);
            this.showStepByHandle(subHandle, "forward");
            this.updateStepperForSub(qHandle);
            return;
          }
        }
        this.advance();
      });
    }

    selectedLabels(block) {
      return Array.from(block.querySelectorAll("[data-answer-select].is-selected"))
        .map((b) => b.dataset.answerLabel)
        .filter(Boolean);
    }

    // Toggling several boxes in quick succession used to fire one POST per click.
    // Those raced and landed out of order, so a selection could be dropped from
    // the cart while still looking selected on screen. Collapse a burst into a
    // single trailing write that reads the final DOM state, and keep a handle on
    // it so navigation can wait for it (see completeJourney).
    //
    // Debounced PER QUESTION, not per controller: a grouped step carries two
    // multi-selects (preferences and the inlined ingredients-to-avoid), and a
    // single shared timer meant a toggle in one block cancelled the other
    // block's pending write — its selections silently never reached the cart.
    // Replacing a question's own pending entry is safe: the map holds only the
    // latest promise per question, and flushPendingWrites() awaits those.
    queueSelectionWrite(block) {
      const handle = block.dataset.questionHandle;
      clearTimeout(this._writeTimers.get(handle));
      this._pendingWrites.set(
        handle,
        new Promise((resolve) => {
          const id = setTimeout(() => {
            this._writeTimers.delete(handle);
            const key = this.cartKeyForStep(handle);
            const joined = this.selectedLabels(block).join(",");
            resolve(
              this.updateCartAttributes({ [key]: joined, [`${key}_label`]: joined }).catch((err) =>
                console.error("[buying-journey] failed to save answers", err)
              )
            );
          }, 250);
          this._writeTimers.set(handle, id);
        })
      );
    }

    async flushPendingWrites() {
      await Promise.all(this._pendingWrites.values());
    }

    clearedSubAttributes(block, chosenSubHandle) {
      const cleared = {};
      block.querySelectorAll("[data-answer-select][data-answer-sub]").forEach((sibling) => {
        const siblingSub = sibling.dataset.answerSub;
        if (!siblingSub || siblingSub === chosenSubHandle) return;
        const subKey = this.cartKeyForStep(siblingSub);
        cleared[subKey] = "";
        cleared[`${subKey}_label`] = "";
        // The sub-question is its own step when ungrouped, and an inline block
        // when grouped. Look for both, or backtracking leaves its cards lit.
        const subEl =
          this.steps.find((s) => s.dataset.step === siblingSub) ||
          Array.from(this.root.querySelectorAll("[data-question-block]")).find(
            (b) => b.dataset.questionHandle === siblingSub
          );
        subEl?.querySelectorAll("[data-answer-select].is-selected").forEach((b) => {
          b.classList.remove("is-selected");
          if (b.getAttribute("aria-checked")) b.setAttribute("aria-checked", "false");
        });
      });
      return cleared;
    }

    advance() {
      if (this.subStack.length > 0) {
        const returnTo = this.subStack.pop();
        this.goToIndex(returnTo + 1, "forward");
        return;
      }
      this.goToIndex(this.currentIndex + 1, "forward");
    }

    goPrev() {
      if (this.subStack.length > 0) {
        const returnTo = this.subStack.pop();
        this.goToIndex(returnTo, "back");
        return;
      }
      this.goToIndex(this.currentIndex - 1, "back");
    }

    goToIndex(idx, direction = "forward") {
      if (idx < 0) return;
      if (idx >= this.sequence.length) {
        this.completeJourney();
        return;
      }
      const handle = this.sequence[idx];
      // Only commit the index and light the chip if a step actually took the screen.
      // Doing it unconditionally is what turned a missing step into a silent desync:
      // the stepper advanced while the wizard stayed put, which reads as "the click
      // did nothing" rather than as the error it is.
      if (!this.showStepByHandle(handle, direction)) return;
      this.currentIndex = idx;
      this.furthestIndex = Math.max(this.furthestIndex, idx);
      this.updateStepper(handle);
    }

    // Exit the outgoing step, swap, then play the incoming one in. Sequential
    // rather than crossfaded: the steps differ wildly in height (the plan
    // picker carries the FAQs), so overlapping them would need an absolutely
    // positioned layer and a height morph that reads as a lurch.
    // Returns whether `handle` now owns the screen, so callers can avoid recording a
    // move that never happened. Already-showing counts as true — the screen is correct.
    showStepByHandle(handle, direction = "forward") {
      const next = this.steps.find((s) => s.dataset.step === handle);
      if (!next) {
        console.warn(`[buying-journey] no rendered step for "${handle}" — staying put.`);
        return false;
      }
      const current = this.steps.find((s) => s.classList.contains("is-active"));
      if (current === next) return true;

      if (this.stepsEl) this.stepsEl.dataset.direction = direction;

      const swap = () => {
        this.steps.forEach((s) => {
          if (s === next) return;
          s.classList.remove("is-active", "is-entering", "is-exiting");
          s.setAttribute("hidden", "");
        });

        next.removeAttribute("hidden");
        next.classList.add("is-active");
        this.preselectPopular(next);
        this.updateNextState(next);
        this.scrollSectionIntoView();
        this.playEnter(next);
        this.isTransitioning = false;
      };

      if (!current || this.reducedMotion()) {
        swap();
        return true;
      }

      this.isTransitioning = true;
      current.classList.add("is-exiting");
      this.after(EXIT_MS, swap);
      return true;
    }

    playEnter(stepEl) {
      if (this.reducedMotion()) return;
      // Re-adding the class alone won't replay the animation; the reflow between
      // remove and add is what restarts it.
      stepEl.classList.remove("is-entering");
      void stepEl.offsetWidth;
      stepEl.classList.add("is-entering");
      this.after(ENTER_MS, () => stepEl.classList.remove("is-entering"));
    }

    scrollSectionIntoView() {
      // Skip smooth-scroll if the section is already in view — rapid clicks
      // otherwise queue overlapping smooth-scroll animations that fight.
      const rect = this.root.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        this.root.scrollIntoView({
          behavior: this.reducedMotion() ? "auto" : "smooth",
          block: "start",
        });
      }
    }

    // Per block, not per step: a grouped step holds several questions and each
    // decides independently whether it has a popular answer to pre-light.
    //
    // Preselecting MUST also write to the cart, in one batched update. On a
    // grouped step singleSelectsAnswered() counts these preselections, so Next
    // unlocks without a single click — and a click is the only other place an
    // answer is written. Class-only preselection let a customer sail through
    // the days/meals screen with nothing saved: the builder then loaded with
    // no days/meals config. The invariant is "cart matches the screen", not
    // "cart matches what was clicked".
    //
    // Disabled blocks (a gated sub-question) are skipped entirely: writing an
    // answer the customer cannot even reach yet would be wrong, and pre-lighting
    // it contradicts the parent answer that keeps it locked.
    preselectPopular(stepEl) {
      const attrs = {};
      stepEl.querySelectorAll("[data-question-block]").forEach((block) => {
        if (block.dataset.questionType === "multi-select") return;
        if (block.hasAttribute("data-question-disabled")) return;
        if (block.querySelector("[data-answer-select].is-selected")) return;
        const popular = block.querySelector("[data-answer-select].buying-journey__answer--popular");
        if (!popular) return;
        popular.classList.add("is-selected");
        const key = this.cartKeyForStep(block.dataset.questionHandle);
        attrs[key] = popular.dataset.answerValue;
        const label = popular.dataset.answerLabel || "";
        if (label) attrs[`${key}_label`] = label;
      });
      if (Object.keys(attrs).length === 0) return;
      this.updateCartAttributes(attrs).catch((err) =>
        console.error("[buying-journey] failed to save preselected answers", err)
      );
    }

    // A chip can stand for several questions at once: consecutive questions sharing
    // a step_label are folded into one chip whose data-step-handles lists them all
    // (see sections/buying-journey.liquid). So "which chip am I on" is a lookup in
    // that list, never an equality test on a single handle.
    stepperIndexFor(handle) {
      return this.stepperItems.findIndex((item) =>
        (item.dataset.stepHandles || "").split(",").includes(handle)
      );
    }

    updateStepper(activeHandle) {
      const activeIdx = this.stepperIndexFor(activeHandle);
      // Compared by position in stepperItems rather than the rendered data-step-index,
      // so the two can't disagree — a question with a blank step_label renders no chip
      // at all, which leaves gaps in the rendered numbering.
      this.stepperItems.forEach((item, idx) => {
        item.classList.toggle("is-current", idx === activeIdx);
        item.classList.toggle("is-complete", activeIdx > -1 && idx < activeIdx);
      });
      this.updateStepperNav(activeIdx);
      this.centerStepperItem(this.stepperItems[activeIdx]);
    }

    // Jump straight to the step a chip stands for. Only reachable through an enabled
    // button, so the "have they been here" question is already settled by
    // updateStepperNav() — this re-checks the index only to refuse a chip this plan's
    // sequence does not contain.
    goToStepperItem(item) {
      if (!item) return;
      const idx = this.sequenceIndexForStepperItem(item);
      if (idx < 0 || idx === this.currentIndex) return;

      // Leaving the sub-question flow entirely: the stack records a step to come BACK to,
      // and the customer has just chosen somewhere else to be. Keeping it would send the
      // next Previous to a step they did not come from.
      this.subStack = [];
      this.goToIndex(idx, idx < this.currentIndex ? "back" : "forward");
    }

    // A chip's position in the stepper is not its position in the journey: the chips are
    // rendered from the canonical plan, and the plan being answered may order or skip
    // questions differently. -1 means this plan never visits that chip's step, so it
    // stays inert rather than navigating somewhere that does not exist.
    sequenceIndexForStepperItem(item) {
      const handles = (item.dataset.stepHandles || "").split(",");
      return this.sequence.findIndex((handle) => handles.includes(handle));
    }

    // Open a chip once its step has been reached, which includes steps AHEAD of the
    // current one after a jump back — the point of furthestIndex. The current chip is
    // left disabled: navigating to where you already are is a no-op that reads as a
    // broken button.
    updateStepperNav(activeIdx) {
      this.stepperItems.forEach((item, idx) => {
        const btn = item.querySelector("[data-stepper-nav]");
        if (!btn) return;
        const seqIdx = this.sequenceIndexForStepperItem(item);
        btn.disabled = seqIdx < 0 || seqIdx > this.furthestIndex || idx === activeIdx;
      });
    }

    // The stepper overflows horizontally on mobile, so later steps sit off-screen.
    // scrollBy on the wrapper (rather than scrollIntoView on the item) keeps the
    // page itself from scrolling as a side effect.
    centerStepperItem(item) {
      const wrap = this.stepperWrapper;
      if (!item || !wrap || wrap.scrollWidth <= wrap.clientWidth) return;
      const wrapRect = wrap.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const delta =
        itemRect.left + itemRect.width / 2 - (wrapRect.left + wrapRect.width / 2);
      wrap.scrollBy({ left: delta, behavior: this.reducedMotion() ? "auto" : "smooth" });
    }

    updateStepperForSub(parentHandle) {
      const parentIdx = this.stepperIndexFor(parentHandle);
      this.stepperItems.forEach((item, idx) => {
        item.classList.toggle("is-current", idx === parentIdx);
      });
    }

    // Finishing the journey flushes the cart write and then hands off to a full
    // page load of the plan builder — a couple of seconds during which the button
    // would otherwise look inert and invite a second click.
    markCompleting() {
      this.isTransitioning = true;
      this.root.classList.add("is-completing");

      const active = this.steps.find((s) => s.classList.contains("is-active"));
      const btn = active?.querySelector("[data-step-next]");
      if (!btn) return;
      btn.classList.add("is-loading");
      btn.setAttribute("aria-busy", "true");
      btn.disabled = true;
    }

    async completeJourney() {
      this.markCompleting();
      // The last question can be a multi-select, so a debounced write may still
      // be in flight. Navigating now would kill it and lose the selection.
      await this.flushPendingWrites();
      const productHandle = this.selectedProductHandle;
      window.location.href = productHandle
        ? `${rootUrl()}products/${productHandle}`
        : `${rootUrl()}pages/meals-plan`;
    }

    async updateCartAttributes(attrs) {
      const res = await fetch(`${window.routes.cart_update_url}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ attributes: attrs }),
      });
      if (!res.ok) throw new Error(`cart update failed: ${res.status}`);
      return res.json();
    }

    dispatchSelected(stepKey, value, detail = {}) {
      this.root.dispatchEvent(new CustomEvent("buying-journey:selected", {
        bubbles: true,
        detail: { stepKey, value, ...detail },
      }));
    }
  }

  window.__BuyingJourneyController = BuyingJourneyController;

  const init = (scope = document) => {
    scope.querySelectorAll("[data-buying-journey]").forEach((root) => {
      if (root.__controller) return;
      root.__controller = new BuyingJourneyController(root);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (e) => init(e.target));
  document.addEventListener("shopify:section:unload", (e) => {
    const root = e.target.querySelector("[data-buying-journey]");
    if (root?.__controller) {
      root.__controller.destroy();
      root.__controller = null;
    }
  });
}
